import type { ConversationMessage, ToolMessage } from './types';
import { isAssistantMessage, isToolMessage, MessageRole } from './types';

/**
 * Drops tool results that no longer answer a preceding assistant tool call.
 *
 * The API rejects the entire request when it sees one, so a single orphan costs the user their
 * whole turn. They appear whenever an assistant message is dropped while its results survive:
 * filtered out of the history as display-only, superseded as a context message, or never
 * assigned its tool_calls because the stream was interrupted. A shape rule rather than a size
 * one, so Conversation.getMessagesForAPI applies it to every request regardless of length.
 */
export function dropOrphanedToolMessages(messages: ConversationMessage[]): ConversationMessage[] {
    const knownToolCallIds = new Set<string>();
    for (const msg of messages) {
        if (isAssistantMessage(msg) && msg.tool_calls) {
            for (const toolCall of msg.tool_calls) {
                knownToolCallIds.add(toolCall.id);
            }
        }
    }
    return messages.filter(
        (msg) => !isToolMessage(msg) || knownToolCallIds.has((msg as ToolMessage).tool_call_id),
    );
}

export const INTERRUPTED_TOOL_RESULT =
    'Interrupted — this tool did not finish, so it has no result.';

/**
 * Answers assistant tool calls that never received a result, the mirror image of
 * dropOrphanedToolMessages. The API rejects the whole request ("No tool output found for
 * function call") when one is missing. They appear when a turn dies mid-batch after a
 * mid-turn save (tab closed, chat remounted, a tool that never resolved) and the conversation
 * is later continued. The synthetic failed result goes right after the call's own results so
 * the model sees the tool did not run.
 */
export function answerUnansweredToolCalls(messages: ConversationMessage[]): ConversationMessage[] {
    const answeredToolCallIds = new Set(
        messages.filter(isToolMessage).map((msg) => (msg as ToolMessage).tool_call_id),
    );
    const result: ConversationMessage[] = [];
    let pendingToolCallIds: string[] = [];
    const flushPending = () => {
        for (const toolCallId of pendingToolCallIds) {
            result.push({
                role: MessageRole.TOOL,
                content: INTERRUPTED_TOOL_RESULT,
                tool_call_id: toolCallId,
                isSuccess: false,
            });
        }
        pendingToolCallIds = [];
    };
    for (const msg of messages) {
        if (!isToolMessage(msg)) {
            flushPending();
        }
        result.push(msg);
        if (isAssistantMessage(msg) && msg.tool_calls) {
            pendingToolCallIds = msg.tool_calls
                .map((toolCall) => toolCall.id)
                .filter((id) => !answeredToolCallIds.has(id));
        }
    }
    flushPending();
    return result;
}

/**
 * Puts each assistant turn's tool results in the order its tool_calls were made. ToolExecutor
 * records a result when its tool finishes — async tools race and review tools run last — so the
 * stored order follows completion, not the model's request.
 */
export function orderToolResultsByCall(messages: ConversationMessage[]): ConversationMessage[] {
    const result: ConversationMessage[] = [];
    let index = 0;
    while (index < messages.length) {
        const msg = messages[index];
        result.push(msg);
        index++;
        if (!isAssistantMessage(msg) || !msg.tool_calls) {
            continue;
        }

        const toolCalls = msg.tool_calls;
        const callPosition = new Map(toolCalls.map((toolCall, i) => [toolCall.id, i]));
        const positionOf = (toolMessage: ToolMessage) =>
            callPosition.get(toolMessage.tool_call_id) ?? toolCalls.length;
        const toolRun: ToolMessage[] = [];
        while (index < messages.length && isToolMessage(messages[index])) {
            toolRun.push(messages[index] as ToolMessage);
            index++;
        }
        toolRun.sort((a, b) => positionOf(a) - positionOf(b));
        result.push(...toolRun);
    }
    return result;
}
